import React, { useState, useEffect, useRef, useCallback } from 'react';

// Design System Components
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Search from 'Components/DesignSystem/Search/Search';
import { Add, Edit, Return } from 'Components/DesignSystem/Iconography/Icons';
import Tag from 'Components/DesignSystem/Tag/Tag';
import Card from 'Components/DesignSystem/Card/Card';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import { TagProps } from 'types/DesignSystem/Tag';

// Types
import { ValueStatus } from '../types';

// Max visible values before showing "View More"
const MAX_VISIBLE_VALUES = 8;
// Initial visible-in-grid count for the "Add More Values" picker per Figma.
const ADD_MORE_INITIAL_VISIBLE = 8;

type BadgeItem = { value: string; type: 'added' | 'removed' | 'catalog' };

export interface AttributeValuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  attributeName: string;
  /** Catalog-side values (rendered as grey badges) */
  catalogValues: string[];
  /** Imported-side values with per-value statuses */
  importedValues: string[];
  /** Parallel to importedValues — per-value status for badge coloring */
  importedValueStatuses?: ValueStatus[];
  /**
   * Pre-session baseline — the imports the parent had BEFORE the user's
   * current edit session (i.e. `attr.values`, ignoring `editedValues`).
   * Used by Reset to restore the original chip set. If omitted, Reset
   * falls back to `importedValues` (legacy behavior), which means any
   * values the user just added via the inline `+` or this modal's
   * picker would persist through Reset — those values were baked into
   * `importedValues` so they'd render as chips on open, but they aren't
   * "original" data and Reset is supposed to undo session edits.
   */
  originalImportedValues?: string[];
  /** Parallel to originalImportedValues — pre-session statuses */
  originalImportedValueStatuses?: ValueStatus[];
  /** Callback when user saves edits; receives the final list of values to keep */
  onSave?: (changes: {
    valuesToKeep: string[];
  }) => void;
  /** When true, the modal opens directly in edit mode */
  isEditMode?: boolean;
  /** Called when user clicks the edit pencil to enter edit mode */
  onEditModeEnter?: () => void;
  /**
   * "Add More Values" picker — when supplied (and modal is editing) the user
   * can expand a panel below the chip list and add catalog values not yet on
   * the row. Loader contract matches MultiSelectWithCreate's `loadOptions`:
   * paged + searchable, returns `{ options, hasMore }`.
   */
  loadValueOptions?: (search: string, page: number) => Promise<{ options: { value: string; label: string }[]; hasMore: boolean }>;
  /** Called when the user clicks a value in the Add More Values grid. */
  onAddValue?: (value: string) => void;
  /** Called when the user clicks the "Create New Value" link in the panel. */
  onCreateNew?: () => void;
}

/**
 * AttributeValuesModal Component
 *
 * Displays all attribute values for a product in a modal dialog.
 * Shows color-coded badges:
 * - Blue (added): New values from import
 * - Red (removed): Values removed in import
 * - Grey (catalog): Existing catalog values / unchanged
 *
 * Edit mode allows removing/restoring values via badge action icons.
 *
 * Figma View: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=12456-32089
 * Figma Edit: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=12456-31405
 */
const AttributeValuesModal = ({
  isOpen,
  onClose,
  productName,
  attributeName,
  catalogValues = [],
  importedValues = [],
  importedValueStatuses = [],
  originalImportedValues,
  originalImportedValueStatuses,
  onSave,
  isEditMode = false,
  onEditModeEnter,
  loadValueOptions,
  onAddValue,
  onCreateNew,
}: AttributeValuesModalProps) => {
  const [showAll, setShowAll] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedBadges, setEditedBadges] = useState<BadgeItem[]>([]);

  // "Add More Values" panel state — collapsed by default; opens when the user
  // clicks `+ Add More Values`. Loader pagination mirrors the inline dropdown
  // (MultiSelectWithCreate) so behaviour is consistent across surfaces.
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addDebouncedSearch, setAddDebouncedSearch] = useState('');
  const [addOptions, setAddOptions] = useState<{ value: string; label: string }[]>([]);
  const [addPage, setAddPage] = useState(1);
  const [addHasMore, setAddHasMore] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addShowAll, setAddShowAll] = useState(false);
  const addSentinelRef = useRef<HTMLDivElement>(null);
  const addDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Combine imported values (blue/red) with catalog-only values (grey).
  // Parameterised so we can build two flavours: the display set (with this
  // session's added values baked in by the parent) and the reset set
  // (pre-session originals only). Behaviour for either input is identical.
  const buildBadgeItems = (
    imports: string[],
    statuses: ValueStatus[]
  ): BadgeItem[] => {
    const items: BadgeItem[] = [];

    // Add imported values with their statuses
    imports.forEach((val, i) => {
      const status = statuses[i] || 'unchanged';
      if (status === 'added') {
        items.push({ value: val, type: 'added' });
      } else if (status === 'removed') {
        items.push({ value: val, type: 'removed' });
      }
    });

    // Add catalog values as grey badges (those not already shown as added/removed)
    catalogValues.forEach((val) => {
      const alreadyShown = items.some((item) => item.value === val);
      if (!alreadyShown) {
        items.push({ value: val, type: 'catalog' });
      }
    });

    // Also include unchanged imported values as catalog style
    imports.forEach((val, i) => {
      const status = statuses[i] || 'unchanged';
      if (status === 'unchanged') {
        const alreadyShown = items.some((item) => item.value === val);
        if (!alreadyShown) {
          items.push({ value: val, type: 'catalog' });
        }
      }
    });

    return items;
  };

  const getBadgeItems = (): BadgeItem[] =>
    buildBadgeItems(importedValues, importedValueStatuses);

  // Reset baseline: prefer the pre-session originals when the parent
  // supplied them; otherwise fall back to the current display set (legacy
  // behaviour for callers that don't pass `originalImportedValues`).
  const getResetBadgeItems = (): BadgeItem[] =>
    originalImportedValues
      ? buildBadgeItems(
          originalImportedValues,
          originalImportedValueStatuses || []
        )
      : getBadgeItems();

  const allBadges = getBadgeItems();

  // Auto-enter edit mode when modal opens and parent is already editing.
  // Also auto-open the "Add More Values" picker in edit mode — when the
  // user is mid-edit and clicks "Show All", they're almost always there
  // to keep editing the value set, not to read a static list. Opening
  // the picker straight away skips the redundant "+ Add More Values"
  // click and lines up the picker beside the already-selected chips
  // (which include any value the user just added via the inline `+`).
  // We DON'T auto-open it in view-mode (`isEditMode=false`) — that's
  // the read-only "show me the catalog list" surface.
  //
  // Deps are intentionally limited to `isOpen` + `isEditMode`. Including
  // `importedValues` / `importedValueStatuses` / `catalogValues` makes the
  // effect re-fire whenever the parent reflects an "add value" from the
  // picker back into its own state — that rebuilds `editedBadges` from
  // props and silently reverts any chips the user had clicked the × on
  // during the same session. `handleGridValueClick` already updates
  // `editedBadges` optimistically, so prop sync mid-session is both
  // unnecessary and load-bearing for the bug above.
  useEffect(() => {
    if (isOpen && isEditMode) {
      setIsEditing(true);
      setEditedBadges(getBadgeItems());
      setShowAll(true);
      setAddPanelOpen(true);
    } else if (!isOpen) {
      // Reset the picker on close so the next open lands fresh — without
      // this, view-mode opens after a previous edit-mode session would
      // inherit a stale `addPanelOpen=true`.
      setAddPanelOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode]);

  // Additive reconciler — when the parent pushes NEW values into props mid-
  // session (most importantly via the "Create New Value" → CreateAttributeValueModal
  // → handleAddAttributeValue chain, which routes back here as a fresh
  // `importedValues` entry), surface them on the chip list without re-deriving
  // from scratch. The seeding effect above is gated on `[isOpen, isEditMode]`
  // for a reason: re-deriving on every prop change would clobber any × removals
  // the user already performed this session (see the deps comment above). This
  // effect only ADDS to `editedBadges`, never resets or removes, so user edits
  // are preserved.
  useEffect(() => {
    if (!isOpen || !isEditing) return;
    setEditedBadges((prev) => {
      const known = new Set(prev.map((b) => b.value));
      const additions: BadgeItem[] = [];
      importedValues.forEach((val, i) => {
        if (known.has(val)) return;
        const status = importedValueStatuses[i] || 'unchanged';
        const type: BadgeItem['type'] =
          status === 'removed' ? 'removed' :
          status === 'added' ? 'added' :
          'catalog';
        additions.push({ value: val, type });
        known.add(val);
      });
      catalogValues.forEach((val) => {
        if (known.has(val)) return;
        additions.push({ value: val, type: 'catalog' });
        known.add(val);
      });
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, [isOpen, isEditing, importedValues, importedValueStatuses, catalogValues]);

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setIsEditing(true);
    setEditedBadges(getBadgeItems());
    setShowAll(true);
    onEditModeEnter?.();
  };

  const handleReset = () => {
    // Restore to the pre-session baseline (drops any values the user
    // added this session via the inline `+` or the modal picker). See
    // `originalImportedValues` prop comment for the why.
    setEditedBadges(getResetBadgeItems());
  };

  const handleRemoveBadge = (index: number) => {
    setEditedBadges((prev) =>
      prev.map((item, i) => (i === index ? { ...item, type: 'removed' as const } : item))
    );
  };

  const handleRestoreBadge = (index: number) => {
    setEditedBadges((prev) =>
      prev.map((item, i) => (i === index ? { ...item, type: 'catalog' as const } : item))
    );
  };

  const handleSave = () => {
    const valuesToKeep = editedBadges
      .filter((b) => b.type !== 'removed')
      .map((b) => b.value);

    onSave?.({ valuesToKeep });
    setIsEditing(false);
  };

  const handleClose = () => {
    setIsEditing(false);
    setShowAll(false);
    closeAddPanel();
    onClose();
  };

  const closeAddPanel = () => {
    setAddPanelOpen(false);
    setAddSearch('');
    setAddDebouncedSearch('');
    setAddOptions([]);
    setAddPage(1);
    setAddHasMore(false);
    setAddShowAll(false);
    if (addDebounceTimer.current) clearTimeout(addDebounceTimer.current);
  };

  // Debounce search input — matches MultiSelectWithCreate's 500ms debounce so
  // typing in either surface feels identical.
  const handleAddSearchChange = (val: string) => {
    setAddSearch(val);
    if (addDebounceTimer.current) clearTimeout(addDebounceTimer.current);
    addDebounceTimer.current = setTimeout(() => {
      setAddDebouncedSearch(val);
      setAddPage(1);
      setAddOptions([]);
    }, 500);
  };

  // Fetch options when the panel opens or search/page changes.
  useEffect(() => {
    if (!addPanelOpen || !loadValueOptions) return;
    setAddLoading(true);
    loadValueOptions(addDebouncedSearch, addPage)
      .then(({ options: newOpts, hasMore }) => {
        setAddHasMore(hasMore);
        setAddOptions((prev) =>
          addPage === 1
            ? newOpts
            : [...prev, ...newOpts.filter((o) => !prev.some((p) => p.value === o.value))]
        );
      })
      .finally(() => setAddLoading(false));
  }, [addPanelOpen, addDebouncedSearch, addPage, loadValueOptions]);

  // IntersectionObserver-driven infinite scroll once the user has expanded
  // the grid past the initial visible window.
  useEffect(() => {
    if (!addSentinelRef.current || !addHasMore || addLoading || !loadValueOptions || !addShowAll) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAddPage((p) => p + 1); },
      { threshold: 0.1 }
    );
    observer.observe(addSentinelRef.current);
    return () => observer.disconnect();
  }, [addHasMore, addLoading, loadValueOptions, addShowAll]);

  // Filter out values already on the chip list — the grid should only ever
  // surface candidates the user hasn't selected yet.
  const selectedValueSet = new Set(
    (isEditing ? editedBadges : allBadges)
      .filter((b) => b.type !== 'removed')
      .map((b) => b.value.toLowerCase())
  );
  const gridOptions = addOptions.filter((o) => !selectedValueSet.has(o.value.toLowerCase()));
  const visibleGridOptions = addShowAll ? gridOptions : gridOptions.slice(0, ADD_MORE_INITIAL_VISIBLE);

  const handleGridValueClick = useCallback((val: string) => {
    // Show the chip immediately (matches the existing 'added' badge style) so
    // the user gets instant feedback without waiting on the parent state hop.
    setEditedBadges((prev) => {
      if (prev.some((b) => b.value === val && b.type !== 'removed')) return prev;
      return [...prev, { value: val, type: 'added' }];
    });
    onAddValue?.(val);
  }, [onAddValue]);

  // Badge data source — conditional on editing
  const displayBadges = isEditing ? editedBadges : allBadges;
  // Track original indices so renderBadge receives the correct index into editedBadges
  const indexedBadges = displayBadges.map((item, i) => ({ item, originalIndex: i }));
  const visibleIndexed = showAll ? indexedBadges : indexedBadges.slice(0, MAX_VISIBLE_VALUES);
  const hasMore = displayBadges.length > MAX_VISIBLE_VALUES;

  /**
   * BadgeItem type -> DS `Tag` props (§ 67).
   *
   * `viewBadgeStyles` and `editBadgeStyles` were byte-identical maps chosen by
   * `isEditing`, so the ternary picked between two copies of the same thing. What
   * actually varies with `isEditing` is whether the chip has an affordance at all,
   * which is now the only thing the branch below decides.
   */
  /* Same rule as `ExpandedRowContent`: the glyph is reserved for `danger`, and the
     tone's meaning goes to assistive tech via `stateLabel` instead of a decorative
     `aria-hidden` glyph. Kept in step deliberately — an "added" chip must not wear a
     glyph in this modal and none in the grid the modal was opened from. */
  const tagPropsForBadge = (
    type: BadgeItem['type'],
  ): Pick<TagProps, 'tone' | 'variant' | 'showToneGlyph' | 'stateLabel'> => {
    switch (type) {
      case 'added': return { tone: 'info', stateLabel: 'added' };
      case 'removed': return { tone: 'danger', showToneGlyph: true, stateLabel: 'removed' };
      /* `catalog` — an existing value, describing rather than reporting a change. */
      default: return { variant: 'meta' };
    }
  };

  const renderBadge = (item: BadgeItem, index: number) => {
    /*
      A `removed` chip's affordance RESTORES; every other chip's REMOVES. Same
      button, opposite meaning — so the glyph and the accessible name both switch,
      where the hand-rolled version switched only the glyph and left the button
      unnamed entirely.
    */
    const isRestore = item.type === 'removed';
    const onRemove = !isEditing
      ? undefined
      : isRestore
        ? () => handleRestoreBadge(index)
        : () => handleRemoveBadge(index);

    return (
      <Tag
        key={`${item.value}-${index}`}
        label={item.value}
        data-testid={`catalog-modal-value-${item.value}`}
        onRemove={onRemove}
        removeIcon={isRestore ? <Return size={12} aria-hidden="true" /> : undefined}
        removeLabel={isRestore ? `Restore ${item.value}` : `Remove ${item.value}`}
        {...tagPropsForBadge(item.type)}
      />
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Attribute Values"
      size="lg"
      data-testid="catalog-attr-values-modal"
      className="tw-max-w-[640px]"
      headerActions={
        onSave != null ? (
          isEditing ? (
            <Button variant="secondary" onClick={handleReset}>
              Reset
            </Button>
          ) : (
            <Button
              variant="subtle"
              size="sm"
              data-testid="modals-attributevaluesmodal-button-2"
              onClick={handleEnterEditMode}
              icon={<Edit />}
            >
              <span className="tw-sr-only">Edit</span>
            </Button>
          )
        ) : undefined
      }
      footer={
        <div className="tw-flex tw-justify-end">
          <Button variant="primary" onClick={isEditing ? handleSave : handleClose}>
            {isEditing ? 'Save Changes' : 'Done'}
          </Button>
        </div>
      }
    >
      {/*
        Product & Attribute — a definition list, not two sentences.

        This was `Typography` + a literal `" - "` span + a bold value, twice, in a
        flex row. Two things went wrong with it as soon as a name got long, and both
        are visible on a real product: the value wrapped under its own label so the
        `-` dangled mid-air, and because the row is `items-center` the second pair
        drifted out of alignment with the first. Long Description values in this
        catalogue are routinely 40+ characters, so that is the normal case, not the
        edge.

        Label above value in a two-column grid fixes the alignment by construction —
        no separator to strand, and the columns cannot shift relative to each other.
        `<dl>` because that is what this is; a screen reader now pairs "Product" with
        its value instead of reading one run-on line. Product gets 2fr because it is
        always the longer of the two.
      */}
      <dl className="tw-m-0 tw-grid tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)] tw-gap-x-6 tw-gap-y-0.5">
        {([
          { label: 'Product', value: productName },
          { label: 'Attribute', value: attributeName },
        ] as const).map(({ label, value }) => (
          <div key={label} className="tw-min-w-0">
            <dt>
              <Typography variant="caption-metadata" className="tw-text-ink-secondary">
                {label}
              </Typography>
            </dt>
            {/* `as="span"`: the value is data, not a heading — `title-header`'s
                default element is an <h4>, and <dd> already carries the meaning. */}
            <dd className="tw-m-0 tw-min-w-0">
              <Typography
                as="span"
                variant="title-header"
                className="tw-block tw-text-ink tw-break-words"
              >
                {value}
              </Typography>
            </dd>
          </div>
        ))}
      </dl>

      {/*
        Values Section.

        The band was `tw-bg-surface-sunken` and the chips inside it are DS `Tag
        variant="meta"`, whose ground is `surface-sunken` — **the same token**, so
        after the § 178 migration every chip here rendered at exactly 1.00:1 against
        its own container and stopped having a shape at all: bare text with an ×.
        (The hand-rolled chips it replaced were white with a `grey-200` border, i.e.
        1.09 fill + a 1.27 outline against this band, which is what was carrying them.)

        The fix belongs on the container, not the chip. `meta` is defined as the quiet
        pair ON `surface`, and it reads at 1.09 there — the same as everywhere else in
        the app. A sunken section holding sunken chips is a nesting mistake the neutral
        ramp has no third step to rescue: there is `surface` and `surface-sunken`, and
        that is all.

        The section keeps its identity through a rule instead of a fill — the same
        argument `Dialog` settled in § 35, where the header's bottom rule turned out to
        be load-bearing precisely because it closes a band without needing to tint it.
      */}
      <div className="tw-px-6 tw-py-4 tw-mt-6 tw--mx-6 tw-border-y tw-border-edge">
        <Typography variant="title-header" className="tw-text-ink-secondary tw-mb-2.5">
          Showing All Attribute Values
        </Typography>
        {/*
          One wrap container, one gap. The catalog chips used to live in a NESTED
          `flex gap-4` inside this `gap-2.5` — which is why the grid read as
          irregular: two chips would sit 10px apart and the next pair 16px, and the
          nested box also wrapped as a unit, so it could break to its own line while
          leaving space beside the chips above it.

          Order is unchanged and still matters: changed values (added / removed)
          first, then the catalog ones. `originalIndex` still indexes into
          `editedBadges`, so every handler targets the same badge it did before —
          sorting here would silently retarget them.
        */}
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
          {[
            ...visibleIndexed.filter((b) => b.item.type !== 'catalog'),
            ...visibleIndexed.filter((b) => b.item.type === 'catalog'),
          ].map(({ item, originalIndex }) => renderBadge(item, originalIndex))}
          {/* View More / View Less */}
          {hasMore && !showAll && (
            <Button
              variant="tertiary"
              size="sm"
              data-testid="modals-attributevaluesmodal-button-3"
              onClick={() => setShowAll(true)}
            >
              View More
            </Button>
          )}
          {hasMore && showAll && (
            <Button
              variant="tertiary"
              size="sm"
              data-testid="modals-attributevaluesmodal-button-4"
              onClick={() => setShowAll(false)}
            >
              View Less
            </Button>
          )}
        </div>

        {/* "Add More Values" trigger + panel — only shown in edit mode AND when
            the parent supplies a loader. Mirrors the inline `+` button on the
            row's value list, but as a richer searchable grid suited to the
            modal's wider canvas. */}
        {isEditing && loadValueOptions && !addPanelOpen && (
          <div className="tw-mt-3">
            <Button
              variant="tertiary"
              size="sm"
              data-testid="modals-attributevaluesmodal-button-5"
              onClick={() => setAddPanelOpen(true)}
              icon={<Add />}
            >
              Add More Values
            </Button>
          </div>
        )}

        {isEditing && loadValueOptions && addPanelOpen && (
          /* DS `Card` owns the surface, border, radius and padding. The hand-roll
             was `bg-white border border-grey-200 rounded px-4 py-3` — the same
             intent with none of the tokens, so it could not follow the DS radius
             ramp or the dark ladder. `elevation="none"`: this is a panel inside a
             dialog, and a card that lifts inside an overlay competes with it. */
          <Card elevation="none" className="tw-mt-4" data-testid="catalog-attr-values-add-panel">
            {/* Top row — search input on the left, Create New Value link on the right */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-3">
              <div className="tw-flex-1 tw-min-w-0 tw-max-w-[320px]">
                <Search
                  placeholder="Search"
                  value={addSearch}
                  onChange={handleAddSearchChange}
                  /* § 288 — `addLoading` is the value fetch; the inequality is the 500ms
                     debounce in front of it. Gated on `loadValueOptions`, which is what
                     the fetch effect itself checks — with no fetcher there is nothing
                     to wait for. */
                  loading={!!loadValueOptions && (addLoading || addSearch !== addDebouncedSearch)}
                  loadingLabel="Searching values"
                  clearable
                  searchIconPosition="end"
                />
              </div>
              {onCreateNew && (
                <Button
                  variant="tertiary"
                  size="sm"
                  data-testid="modals-attributevaluesmodal-button-6"
                  onClick={onCreateNew}
                >
                  Create New Value
                </Button>
              )}
            </div>

            <Typography variant="title-header" className="tw-text-ink-secondary tw-mb-2.5">
              Select More Attribute Values
            </Typography>

            <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
              {/*
                These are BUTTONS that add a value, so they are `Button`, not
                `ChoiceChip`. ChoiceChip is the visually obvious pick and it is the
                wrong one: it is a toggle that renders `aria-pressed`, and the grid
                filters out anything already chosen — so every chip here would
                permanently announce `aria-pressed="false"`, describing a toggle
                state that does not exist. `secondary` at `sm` is 28px (clears SC
                2.5.8), carries the DS focus ring the hand-rolled pill had none of,
                and says what it is.

                One testid per element, keyed on the value. All of these shared a
                single static `-button-7`, so a spec could only reach one by index.
              */}
              {visibleGridOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant="secondary"
                  size="sm"
                  data-testid={`catalog-attr-values-option-${opt.value}`}
                  onClick={() => handleGridValueClick(opt.value)}
                  title={opt.value}
                  className="tw-max-w-[200px]"
                >
                  {/* `block`, not a bare inline span: `text-overflow` needs a box with
                      a resolved width, and an inline span inside a flex item has
                      neither. With Button's wrapper now `min-w-0`, this shrinks to the
                      button and ellipsises instead of spilling. */}
                  <span className="tw-block tw-truncate">{opt.label}</span>
                </Button>
              ))}

              {!addLoading && gridOptions.length === 0 && (
                <Typography variant="body-primary" className="tw-text-ink-secondary tw-italic">
                  No values available
                </Typography>
              )}

              {gridOptions.length > ADD_MORE_INITIAL_VISIBLE && !addShowAll && (
                <Button
                  variant="tertiary"
                  size="sm"
                  data-testid="modals-attributevaluesmodal-button-8"
                  onClick={() => setAddShowAll(true)}
                >
                  View more
                </Button>
              )}
            </div>

            {/* Infinite-scroll sentinel — only active after the user has chosen
                to view more, otherwise we keep the panel compact. */}
            {addShowAll && addHasMore && !addLoading && (
              <div ref={addSentinelRef} className="tw-h-1" />
            )}

            {addLoading && (
              <div className="tw-mt-3 tw-flex tw-justify-center">
                <Spinner size="sm" label="Loading attribute values" showLabel />
              </div>
            )}
          </Card>
        )}
      </div>
    </Dialog>
  );
};

export default AttributeValuesModal;
